import { useState } from 'react'
import { useContacts } from '@/core/hooks/useContacts'
import type { ContactRecord, ContactType, InteractionRecord } from '@/core/types'
import {
  Plus, Search, X, Mail, Phone, Linkedin, Building2,
  MessageSquare, Trash2, ChevronDown, ChevronUp, Edit2, Check, ExternalLink
} from 'lucide-react'
import { format } from 'date-fns'

const CONTACT_TYPES: { value: ContactType; label: string; color: string }[] = [
  { value: 'recruiter', label: 'Recruiter', color: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20' },
  { value: 'hiring_manager', label: 'Hiring Manager', color: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-500/10 dark:text-purple-400 dark:border-purple-500/20' },
  { value: 'referral', label: 'Referral', color: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20' },
  { value: 'other', label: 'Other', color: 'bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-500/10 dark:text-gray-400 dark:border-gray-500/20' },
]

function contactTypeBadge(type: ContactType) {
  const ct = CONTACT_TYPES.find(c => c.value === type)
  if (!ct) return null
  return <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full border ${ct.color}`}>{ct.label}</span>
}

const INTERACTION_TYPE_LABELS: Record<InteractionRecord['type'], string> = {
  email: 'Email',
  call: 'Call',
  meeting: 'Meeting',
  message: 'Message',
  other: 'Other'
}

export function ContactsPage() {
  const { contacts, isLoading, error, create, update, remove, addInteraction, removeInteraction, clearError } = useContacts()
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState<ContactType | ''>('')
  const [showAddForm, setShowAddForm] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [showInteractionForm, setShowInteractionForm] = useState<string | null>(null)

  // Add contact form state
  const [newContact, setNewContact] = useState({
    name: '', role: '', email: '', phone: '', linkedInUrl: '',
    contactType: 'recruiter' as ContactType, company: '', notes: ''
  })

  // Interaction form state
  const [newInteraction, setNewInteraction] = useState({
    date: new Date().toISOString().split('T')[0],
    type: 'email' as InteractionRecord['type'],
    summary: ''
  })

  // Edit form state
  const [editForm, setEditForm] = useState<Partial<ContactRecord>>({})

  // Filter contacts
  const filtered = contacts.filter(c => {
    if (filterType && c.contactType !== filterType) return false
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      return c.name.toLowerCase().includes(q) ||
        c.company?.toLowerCase().includes(q) ||
        c.role.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q)
    }
    return true
  })

  function handleCreateContact() {
    if (!newContact.name.trim()) return
    create({
      name: newContact.name.trim(),
      role: newContact.role.trim(),
      email: newContact.email.trim() || undefined,
      phone: newContact.phone.trim() || undefined,
      linkedInUrl: newContact.linkedInUrl.trim() || undefined,
      contactType: newContact.contactType,
      company: newContact.company.trim() || undefined,
      notes: newContact.notes.trim()
    })
    setNewContact({ name: '', role: '', email: '', phone: '', linkedInUrl: '', contactType: 'recruiter', company: '', notes: '' })
    setShowAddForm(false)
  }

  function startEdit(contact: ContactRecord) {
    setEditingId(contact.id)
    setEditForm({
      name: contact.name,
      role: contact.role,
      email: contact.email,
      phone: contact.phone,
      linkedInUrl: contact.linkedInUrl,
      contactType: contact.contactType,
      company: contact.company,
      notes: contact.notes
    })
  }

  function handleSaveEdit(id: string) {
    update(id, editForm)
    setEditingId(null)
    setEditForm({})
  }

  function handleAddInteraction(contactId: string) {
    if (!newInteraction.summary.trim()) return
    addInteraction(contactId, {
      date: newInteraction.date,
      type: newInteraction.type,
      summary: newInteraction.summary.trim()
    })
    setNewInteraction({ date: new Date().toISOString().split('T')[0], type: 'email', summary: '' })
    setShowInteractionForm(null)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Contacts</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Manage recruiters, hiring managers, and referrals
          </p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium shadow-sm"
        >
          <Plus className="w-4 h-4" />
          Add Contact
        </button>
      </div>

      {/* Search & Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search contacts..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-border rounded-lg text-sm bg-card focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          value={filterType}
          onChange={e => setFilterType(e.target.value as ContactType | '')}
          className="px-3 py-2 border border-border rounded-lg text-sm bg-card focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Types</option>
          {CONTACT_TYPES.map(ct => (
            <option key={ct.value} value={ct.value}>{ct.label}</option>
          ))}
        </select>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-700">{error}</p>
          <button onClick={clearError}><X className="w-4 h-4 text-red-500" /></button>
        </div>
      )}

      {/* Add Contact Form */}
      {showAddForm && (
        <div className="bg-card border border-border rounded-xl p-5 space-y-4 shadow-sm">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground">New Contact</h3>
            <button onClick={() => setShowAddForm(false)} className="p-1 text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input
              type="text" placeholder="Name *" value={newContact.name}
              onChange={e => setNewContact(c => ({ ...c, name: e.target.value }))}
              className="px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" autoFocus
            />
            <input
              type="text" placeholder="Role *" value={newContact.role}
              onChange={e => setNewContact(c => ({ ...c, role: e.target.value }))}
              className="px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="email" placeholder="Email" value={newContact.email}
              onChange={e => setNewContact(c => ({ ...c, email: e.target.value }))}
              className="px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="tel" placeholder="Phone" value={newContact.phone}
              onChange={e => setNewContact(c => ({ ...c, phone: e.target.value }))}
              className="px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="url" placeholder="LinkedIn URL" value={newContact.linkedInUrl}
              onChange={e => setNewContact(c => ({ ...c, linkedInUrl: e.target.value }))}
              className="px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="text" placeholder="Company" value={newContact.company}
              onChange={e => setNewContact(c => ({ ...c, company: e.target.value }))}
              className="px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center gap-3">
            <label className="text-xs font-medium text-muted-foreground">Type</label>
            <div className="flex gap-2">
              {CONTACT_TYPES.map(ct => (
                <button
                  key={ct.value}
                  onClick={() => setNewContact(c => ({ ...c, contactType: ct.value }))}
                  className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                    newContact.contactType === ct.value
                      ? ct.color + ' ring-2 ring-offset-1 ring-current font-medium'
                      : 'border-border text-muted-foreground hover:bg-accent'
                  }`}
                >
                  {ct.label}
                </button>
              ))}
            </div>
          </div>
          <textarea
            placeholder="Notes..." value={newContact.notes}
            onChange={e => setNewContact(c => ({ ...c, notes: e.target.value }))}
            className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 h-20 resize-vertical"
          />
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setShowAddForm(false)}
              className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateContact}
              disabled={!newContact.name.trim()}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              Add Contact
            </button>
          </div>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Empty State */}
      {!isLoading && filtered.length === 0 && !showAddForm && (
        <div className="text-center py-12">
          <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center mx-auto mb-3">
            <Search className="w-6 h-6 text-muted-foreground" />
          </div>
          <h3 className="text-sm font-medium text-foreground mb-1">
            {searchQuery || filterType ? 'No contacts found' : 'No contacts yet'}
          </h3>
          <p className="text-xs text-muted-foreground">
            {searchQuery || filterType ? 'Try adjusting your search or filters' : 'Add your first contact to start building your network'}
          </p>
        </div>
      )}

      {/* Contact Cards */}
      <div className="space-y-2">
        {filtered.map(contact => (
          <div key={contact.id} className="bg-card border border-border rounded-xl overflow-hidden">
            {/* Card Header */}
            <div
              className="px-5 py-4 flex items-center gap-4 cursor-pointer hover:bg-accent/50 transition-colors"
              onClick={() => setExpandedId(expandedId === contact.id ? null : contact.id)}
            >
              {/* Avatar */}
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white font-semibold flex items-center justify-center text-sm flex-shrink-0">
                {contact.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground truncate">{contact.name}</span>
                  {contactTypeBadge(contact.contactType)}
                </div>
                <div className="flex items-center gap-3 mt-0.5">
                  <span className="text-xs text-muted-foreground truncate">{contact.role}</span>
                  {contact.company && (
                    <>
                      <span className="text-xs text-muted-foreground">at</span>
                      <span className="text-xs text-muted-foreground truncate flex items-center gap-0.5">
                        <Building2 className="w-3 h-3" />
                        {contact.company}
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* Quick actions */}
              <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                {contact.email && (
                  <a href={`mailto:${contact.email}`} className="p-1.5 text-muted-foreground hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Send email">
                    <Mail className="w-4 h-4" />
                  </a>
                )}
                {contact.phone && (
                  <a href={`tel:${contact.phone}`} className="p-1.5 text-muted-foreground hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors" title="Call">
                    <Phone className="w-4 h-4" />
                  </a>
                )}
                {contact.linkedInUrl && (
                  <a href={contact.linkedInUrl} target="_blank" rel="noopener noreferrer" className="p-1.5 text-muted-foreground hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="LinkedIn profile">
                    <Linkedin className="w-4 h-4" />
                  </a>
                )}
              </div>

              {/* Expand icon */}
              {expandedId === contact.id ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
            </div>

            {/* Expanded Details */}
            {expandedId === contact.id && (
              <div className="px-5 pb-5 space-y-4 border-t border-border">
                {editingId === contact.id ? (
                  /* Edit Mode */
                  <div className="pt-4 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <input type="text" value={editForm.name || ''} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} className="px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Name" />
                      <input type="text" value={editForm.role || ''} onChange={e => setEditForm(f => ({ ...f, role: e.target.value }))} className="px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Role" />
                      <input type="email" value={editForm.email || ''} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} className="px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Email" />
                      <input type="tel" value={editForm.phone || ''} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} className="px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Phone" />
                      <input type="url" value={editForm.linkedInUrl || ''} onChange={e => setEditForm(f => ({ ...f, linkedInUrl: e.target.value }))} className="px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="LinkedIn URL" />
                      <input type="text" value={editForm.company || ''} onChange={e => setEditForm(f => ({ ...f, company: e.target.value }))} className="px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Company" />
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="text-xs font-medium text-muted-foreground">Type</label>
                      <div className="flex gap-2">
                        {CONTACT_TYPES.map(ct => (
                          <button key={ct.value} onClick={() => setEditForm(f => ({ ...f, contactType: ct.value }))}
                            className={`px-3 py-1 text-xs rounded-full border transition-colors ${editForm.contactType === ct.value ? ct.color + ' ring-2 ring-offset-1 ring-current font-medium' : 'border-border text-muted-foreground hover:bg-accent'}`}>
                            {ct.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <textarea value={editForm.notes || ''} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 h-20 resize-vertical" placeholder="Notes" />
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setEditingId(null)} className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg">Cancel</button>
                      <button onClick={() => handleSaveEdit(contact.id)} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1"><Check className="w-3.5 h-3.5" />Save</button>
                    </div>
                  </div>
                ) : (
                  /* View Mode */
                  <>
                    {/* Contact Details */}
                    <div className="pt-4">
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        {contact.email && (
                          <div className="flex items-center gap-2">
                            <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                            <a href={`mailto:${contact.email}`} className="text-blue-600 hover:underline">{contact.email}</a>
                          </div>
                        )}
                        {contact.phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-3.5 h-3.5 text-muted-foreground" />
                            <span>{contact.phone}</span>
                          </div>
                        )}
                        {contact.linkedInUrl && (
                          <div className="flex items-center gap-2">
                            <Linkedin className="w-3.5 h-3.5 text-muted-foreground" />
                            <a href={contact.linkedInUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-0.5">
                              LinkedIn <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        )}
                      </div>
                      {contact.notes && (
                        <div className="mt-3 text-sm text-muted-foreground bg-accent/50 rounded-lg p-3">
                          {contact.notes}
                        </div>
                      )}
                    </div>

                    {/* Actions Row */}
                    <div className="flex items-center justify-between">
                      <div className="flex gap-2">
                        <button onClick={() => startEdit(contact)} className="flex items-center gap-1 px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors">
                          <Edit2 className="w-3.5 h-3.5" /> Edit
                        </button>
                        <button onClick={() => setShowInteractionForm(contact.id)} className="flex items-center gap-1 px-3 py-1.5 text-xs text-blue-600 hover:bg-blue-50 rounded-lg transition-colors font-medium">
                          <MessageSquare className="w-3.5 h-3.5" /> Log Interaction
                        </button>
                      </div>
                      <button onClick={() => { remove(contact.id); setExpandedId(null) }} className="flex items-center gap-1 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </div>

                    {/* Interaction Log */}
                    <div>
                      <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                        Interaction History ({contact.interactions.length})
                      </h4>
                      {contact.interactions.length === 0 && !showInteractionForm && (
                        <p className="text-xs text-muted-foreground italic">No interactions logged</p>
                      )}
                      <div className="space-y-2">
                        {contact.interactions
                          .sort((a, b) => b.date.localeCompare(a.date))
                          .map(interaction => (
                          <div key={interaction.id} className="flex items-start gap-3 bg-accent/30 rounded-lg p-3 group">
                            <div className="text-xs text-muted-foreground w-20 flex-shrink-0 pt-0.5">
                              {format(new Date(interaction.date), 'MMM d, yyyy')}
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className="inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded bg-accent text-muted-foreground uppercase tracking-wider">
                                {INTERACTION_TYPE_LABELS[interaction.type]}
                              </span>
                              <p className="text-sm text-foreground mt-1">{interaction.summary}</p>
                            </div>
                            <button
                              onClick={() => removeInteraction(contact.id, interaction.id)}
                              className="p-1 text-muted-foreground hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>

                      {/* Add Interaction Form */}
                      {showInteractionForm === contact.id && (
                        <div className="mt-2 bg-blue-50/50 dark:bg-blue-500/5 rounded-lg p-3 space-y-2">
                          <div className="flex gap-2">
                            <input type="date" value={newInteraction.date} onChange={e => setNewInteraction(i => ({ ...i, date: e.target.value }))} className="px-2 py-1.5 border border-border rounded text-sm" />
                            <select value={newInteraction.type} onChange={e => setNewInteraction(i => ({ ...i, type: e.target.value as InteractionRecord['type'] }))} className="px-2 py-1.5 border border-border rounded text-sm">
                              <option value="email">Email</option>
                              <option value="call">Call</option>
                              <option value="meeting">Meeting</option>
                              <option value="message">Message</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                          <input type="text" placeholder="Summary of interaction..." value={newInteraction.summary} onChange={e => setNewInteraction(i => ({ ...i, summary: e.target.value }))} className="w-full px-2 py-1.5 border border-border rounded text-sm" autoFocus />
                          <div className="flex gap-2 justify-end">
                            <button onClick={() => setShowInteractionForm(null)} className="px-3 py-1 text-xs text-muted-foreground hover:bg-accent rounded">Cancel</button>
                            <button onClick={() => handleAddInteraction(contact.id)} disabled={!newInteraction.summary.trim()} className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">Add</button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Linked Applications */}
                    {contact.linkedApplicationIds.length > 0 && (
                      <div>
                        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                          Linked Applications ({contact.linkedApplicationIds.length})
                        </h4>
                        <p className="text-xs text-muted-foreground">{contact.linkedApplicationIds.length} application(s) linked</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
